import React, { useEffect, useRef, useState, useCallback } from 'react';
import { DataPoint } from '../../utils/MarketDataGenerator';
import { X, RotateCcw } from 'lucide-react';
import { CoreHUD } from '../layout/CoreHUD';
import { getTimeframeSeparation, getPeriodLabel, getSeparatorIndices, getDynamicPeriodSeparation } from '../../utils/timeUtils';
import { useFlowaesStore } from '../../store/useFlowaesStore';
import { CandleCountdown } from "../ui/ClockComponents";
import { useWaveGeometry, useWaveColors } from '../../utils/waveGeometry';


const formatAbbrev = (val: number, decimals: number) => {
  if (Math.abs(val) >= 1000) {
    return (val / 1000).toFixed(1) + 'K';
  }
  return val.toFixed(decimals);
};

const hexToRgba = (hex: string, opacity: number) => {
  if (!hex) return 'rgba(255,255,255,0.1)';
  const c = hex.replace('#', '');
  if (c.length !== 6) return hex;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const getTickInterval = (min: number, max: number, maxTicks: number) => {
  const range = Math.abs(max - min);
  if (range <= 0 || !isFinite(range)) return 1;
  const roughStep = range / maxTicks;
  const stepPower = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalizedStep = roughStep / stepPower;
  let niceStep = 1;
  if (normalizedStep > 5) niceStep = 10;
  else if (normalizedStep > 2) niceStep = 5;
  else if (normalizedStep > 1) niceStep = 2;
  return niceStep * stepPower;
};

const getTimeTickInterval = (min: number, max: number, maxTicks: number) => {
  const range = Math.abs(max - min);
  if (range <= 0 || !isFinite(range)) return 60000;
  const rough = range / maxTicks;
  const steps = [
    1000, 2000, 5000, 10000, 15000, 30000,
    60000, 120000, 300000, 600000, 900000, 1800000,
    3600000, 7200000, 14400000, 28800000,
    86400000, 86400000 * 2, 86400000 * 7
  ];
  for (const step of steps) {
    if (step >= rough) return step;
  }
  return steps[steps.length - 1];
};

const formatTime = (ts: number, rangeMs: number) => {
  const d = new Date(ts);
  if (rangeMs > 24 * 60 * 60 * 1000 * 3) {
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

interface Floor2DChartProps {
  isOpen: boolean;
  onClose: () => void;
  data: any[];
  symbol?: string;
  timeframe?: string;
  isEditingSymbol?: boolean;
  setIsEditingSymbol?: (val: boolean) => void;
  setSymbol?: (val: string) => void;
  lastPrice?: number | null;
  priceChange?: number;
  latencyMs?: number | null;
}

export const Floor2DChart: React.FC<Floor2DChartProps> = ({ isOpen, onClose, data, symbol = "BTCUSDT", timeframe = "1m", isEditingSymbol = false, setIsEditingSymbol = () => {}, setSymbol = () => {}, lastPrice = null, priceChange = 0, latencyMs = null }) => {
  const colorUp = useFlowaesStore(s => s.colorUp);
  const colorDown = useFlowaesStore(s => s.colorDown);
  const buyersColor = useFlowaesStore(s => s.buyersColor) || '#00ff88';
  const sellersColor = useFlowaesStore(s => s.sellersColor) || '#ff3b69';
  const gridColor = useFlowaesStore(s => s.gridColor);
  const gridOpacity = useFlowaesStore(s => s.gridOpacity);

  const gridThickness = useFlowaesStore(s => s.gridThickness);
  const showPeriodSeparators = useFlowaesStore(s => s.showPeriodSeparators);
  const periodColor = useFlowaesStore(s => s.periodColor);
  const periodOpacity = useFlowaesStore(s => s.periodOpacity);
  const periodLineStyle = useFlowaesStore(s => s.periodLineStyle);
  const periodLineWidth = useFlowaesStore(s => s.periodLineWidth);
  
  
  const colorIntensity = useFlowaesStore(s => s.colorIntensity);
  const colorContrast = useFlowaesStore(s => s.colorContrast);
  const width3D = useFlowaesStore(s => s.width3D);
  const heightScale3D = useFlowaesStore(s => s.heightScale3D);
  const zLimit3D = useFlowaesStore(s => s.zLimit3D);
  const momentumSensitivity = useFlowaesStore(s => s.momentumSensitivity);
  const curveTension = useFlowaesStore(s => s.curveTension);
  const historyShadowStrength = useFlowaesStore(s => s.historyShadowStrength);
  const sideHistoryShadowStrength = useFlowaesStore(s => s.sideHistoryShadowStrength);
  const sma10Period = useFlowaesStore(s => s.sma10Period);
  const sma20Period = useFlowaesStore(s => s.sma20Period);
  const sma50Period = useFlowaesStore(s => s.sma50Period);
  const showSMA10 = useFlowaesStore(s => s.showSMA10);
  const showSMA20 = useFlowaesStore(s => s.showSMA20);
  const showSMA50 = useFlowaesStore(s => s.showSMA50);

  const { fullColors3D } = useWaveColors(data, colorUp, colorDown, colorIntensity, colorContrast);
  const layout = React.useMemo(() => ({ width3D, heightScale3D, zLimit3D, startX: -width3D / 2 }), [width3D, heightScale3D, zLimit3D]);
  const config = React.useMemo(() => ({
    momentumSensitivity, curveTension, historyShadowStrength, sideHistoryShadowStrength,
    sma10Period, sma20Period, sma50Period, showSMA10, showSMA20, showSMA50, fullColors3D
  }), [momentumSensitivity, curveTension, historyShadowStrength, sideHistoryShadowStrength, sma10Period, sma20Period, sma50Period, showSMA10, showSMA20, showSMA50, fullColors3D]);

  const { smoothedPoints, smoothedColorsFloor, zScale, smoothedPtsCount } = useWaveGeometry(data, layout, config);

    const [labelOpacity, setLabelOpacity] = useState(0);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLabelOpacity(1);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = setTimeout(() => {
        setLabelOpacity(0);
      }, 20000);
    } else {
      setLabelOpacity(0);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    }
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [isOpen]);

  const canvasRef = useRef<HTMLCanvasElement>(null);const wrapperRef = useRef<HTMLDivElement>(null);
  
  // Pan and Zoom
  const transformRef = useRef({ panX: 0, panY: 0, zoom: 1 });
  const isDragging = useRef(false);
  const lastPointer = useRef({ x: 0, y: 0 });
  const drawRef = useRef<(() => void) | null>(null);
  const [liveInfo, setLiveInfo] = useState<{ price: number; momentum: number; closeTime?: number } | null>(null);

  const resetTransform = useCallback(() => {
    transformRef.current = { panX: 0, panY: 0, zoom: 1 };
    if (drawRef.current) requestAnimationFrame(drawRef.current);
  }, []);

  const getPointTime = useCallback((p: DataPoint) => p.timestamp || p.closeTime || (p.time ? Date.now() - (1 - p.time) * 3600000 : 0), []);

  const timeMin = data.length > 0 ? getPointTime(data[0]) : 0;
  const timeMax = data.length > 0 ? getPointTime(data[data.length - 1]) : 1;
  const timeDiff = (timeMax - timeMin) || 1;
  const startX = -width3D / 2;

  useEffect(() => {
    if (data.length > 0) {
      const last = data[data.length - 1];
      const t = getPointTime(last);
      setLiveInfo({
        price: last.close ?? last.price,
        momentum: last.momentum ?? 0,
        closeTime: last.closeTime
      });
    }
  }, [data, getPointTime]);

  useEffect(() => {
    if (!isOpen || !canvasRef.current || !wrapperRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    const rgbaGrid = hexToRgba(gridColor, gridOpacity);

    const draw = () => {
      if (!wrapperRef.current) return;
      const width = wrapperRef.current.clientWidth;
      const height = wrapperRef.current.clientHeight;
      if (width === 0 || height === 0) return;

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      
      // Deep dark canvas background
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
      
      // 1. FIXED VIEWPORT BOX (Always anchored on screen with fixed margins for labels)
      const margin = { left: 0, right: 0, top: 0, bottom: 0 };
      const boxLeft = margin.left;
      const boxTop = margin.top;
      const boxW = Math.max(100, width - margin.left - margin.right);
      const boxH = Math.max(100, height - margin.top - margin.bottom);
      const boxRight = boxLeft + boxW;
      const boxBottom = boxTop + boxH;

      const { panX, panY, zoom } = transformRef.current;

      // Base coordinate scaling (Uniform scaling to preserve 3D aspect ratio)
      const scaleX = boxW / width3D;
      const scaleY = boxH / (zLimit3D * 2);
      const baseScale = Math.min(scaleX, scaleY) * zoom;

      // Pan limits constraint
      const dataScreenW = width3D * baseScale;
      const dataScreenH = (zLimit3D * 2) * baseScale;
      const limitX = Math.max(0, dataScreenW / 2 + boxW * 0.3);
      const limitY = Math.max(0, dataScreenH / 2 + boxH * 0.3);
      if (transformRef.current.panX > limitX) transformRef.current.panX = limitX;
      if (transformRef.current.panX < -limitX) transformRef.current.panX = -limitX;
      if (transformRef.current.panY > limitY) transformRef.current.panY = limitY;
      if (transformRef.current.panY < -limitY) transformRef.current.panY = -limitY;

      // Center of content inside the fixed viewport with pan applied
      const centerX = boxLeft + boxW / 2 + transformRef.current.panX;
      const centerY = boxTop + boxH / 2 + transformRef.current.panY;

      // Transformations:
      // X-Axis = Time (3D X coordinate: -width3D/2 to +width3D/2) -> Left is Past, Right is Present
      // Y-Axis = Momentum (3D Z coordinate: -zLimit3D to +zLimit3D) -> Bottom is Positive (Buyers +), Top is Negative (Sellers -)
      const toScreenX = (dataX: number) => centerX + dataX * baseScale;
      const toScreenY = (dataZ: number) => centerY + dataZ * baseScale;

      const toDataX = (screenX: number) => (screenX - centerX) / baseScale;
      const toDataZ = (screenY: number) => (screenY - centerY) / baseScale;

      const timeToDataX = (t: number) => {
        const timeRatio = (t - timeMin) / timeDiff;
        return startX + timeRatio * width3D;
      };

      const dataXToTime = (dataX: number) => {
        const ratio = (dataX - startX) / width3D;
        return timeMin + ratio * timeDiff;
      };

      // Visible data boundaries at the fixed box edges
      const visDataXMin = toDataX(boxLeft);
      const visDataXMax = toDataX(boxRight);
      const visTimeMin = dataXToTime(visDataXMin);
      const visTimeMax = dataXToTime(visDataXMax);

      const visZBottom = toDataZ(boxBottom);
      const visZTop = toDataZ(boxTop);
      const visMomMin = visZBottom / zScale;
      const visMomMax = visZTop / zScale;

      // 2. Draw Fixed Viewport Background
      ctx.fillStyle = '#000000';
      ctx.fillRect(boxLeft, boxTop, boxW, boxH);

      // 3. CLIPPED CONTENT REGION (Only internal drawing can occur here)
      ctx.save();
      ctx.beginPath();
      ctx.rect(boxLeft, boxTop, boxW, boxH);
      ctx.clip(); // Strict bounding: nothing leaves the viewport frame!

      // Draw Zone Tints (Sellers on Top, Buyers on Bottom relative to Z=0)
      const zeroScreenY = toScreenY(0);
      const clampZeroY = Math.max(boxTop, Math.min(boxBottom, zeroScreenY));
      
      if (clampZeroY > boxTop) {
        ctx.fillStyle = hexToRgba(sellersColor, 0.05);
        ctx.fillRect(boxLeft, boxTop, boxW, clampZeroY - boxTop);
      }
      if (clampZeroY < boxBottom) {
        ctx.fillStyle = hexToRgba(buyersColor, 0.05);
        ctx.fillRect(boxLeft, clampZeroY, boxW, boxBottom - clampZeroY);
      }

      if (clampZeroY > boxTop && clampZeroY < boxBottom) {
        ctx.beginPath();
        ctx.setLineDash([4, 4]);
        ctx.moveTo(boxLeft, clampZeroY);
        ctx.lineTo(boxRight, clampZeroY);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);
      }

      
      // Draw Period Separators
      if (showPeriodSeparators && data.length > 1) {
        ctx.save();
        const rgbaPeriod = hexToRgba(periodColor || '#ffffff', periodOpacity || 0.3);
        ctx.strokeStyle = rgbaPeriod;
        ctx.lineWidth = (periodLineWidth || 1) * 2;
        if (periodLineStyle === 'dashed') ctx.setLineDash([4, 4]);
        else if ((periodLineStyle as any) === 'dotted') ctx.setLineDash([2, 4]);
        
        ctx.beginPath();
        const baseSepType = getTimeframeSeparation(timeframe);
        const msPerPixel = timeDiff / (width3D * baseScale);
        const visibleMs = boxW * msPerPixel;
        const dynamicSepType = getDynamicPeriodSeparation(baseSepType, visibleMs, boxW, 40);
        const sepIndices = getSeparatorIndices(data, timeframe, dynamicSepType);
        
        for (const sep of sepIndices) {
          const ptTime = data[sep.index].time; 
          const px = toScreenX(startX + ptTime * width3D);
          if (px >= boxLeft && px <= boxRight) {
            ctx.moveTo(px, boxTop);
            ctx.lineTo(px, boxBottom);
            
            // Draw Label
            ctx.fillStyle = rgbaPeriod;
            ctx.font = '600 11px "Inter", ui-sans-serif, system-ui, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(sep.label, px, boxTop + 12);
          }
        }
        
        ctx.stroke();
        ctx.restore();
      }

      // Draw Grid Lines inside Viewport
      ctx.lineWidth = gridThickness;
      ctx.strokeStyle = rgbaGrid;

      // Vertical Time Lines
      const timeStep = getTimeTickInterval(visTimeMin, visTimeMax, Math.max(5, Math.floor(boxW / 80)));
      const firstTime = Math.floor(visTimeMin / timeStep) * timeStep;
      for (let t = firstTime; t <= visTimeMax + timeStep * 0.5; t += timeStep) {
        const px = toScreenX(timeToDataX(t));
        if (px >= boxLeft - 0.5 && px <= boxRight + 0.5) {
          ctx.beginPath();
          ctx.moveTo(px, boxTop);
          ctx.lineTo(px, boxBottom);
          ctx.stroke();
        }
      }

      // Horizontal Momentum Lines
      
      const realVisMomMin = Math.min(visMomMin, visMomMax);
      const realVisMomMax = Math.max(visMomMin, visMomMax);
      const momStep = getTickInterval(realVisMomMin, realVisMomMax, Math.max(5, Math.floor(boxH / 50)));
      const firstMom = Math.floor(realVisMomMin / momStep) * momStep;
      for (let m = firstMom; m <= realVisMomMax + momStep * 0.5; m += momStep) {
        const py = toScreenY(m * zScale);
        if (py >= boxTop - 0.5 && py <= boxBottom + 0.5) {
          ctx.beginPath();
          ctx.moveTo(boxLeft, py);
          ctx.lineTo(boxRight, py);
          ctx.stroke();
        }
      }

      // Draw Equilibrium Center Line (Momentum = 0)
      if (zeroScreenY >= boxTop && zeroScreenY <= boxBottom) {
        ctx.save();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(boxLeft, zeroScreenY);
        ctx.lineTo(boxRight, zeroScreenY);
        ctx.stroke();
        ctx.restore();
      }

      // Draw Floor Curve Projection (Momentum vs Time)
      if (smoothedPtsCount > 0) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (let i = 0; i < smoothedPtsCount - 1; i++) {
          const idx1 = i * 3;
          const idx2 = (i + 1) * 3;
           
          const r = smoothedColorsFloor[idx1] ?? 1;
          const g = smoothedColorsFloor[idx1+1] ?? 1;
          const b = smoothedColorsFloor[idx1+2] ?? 1;
          
          ctx.beginPath();
          ctx.moveTo(toScreenX(smoothedPoints[idx1]), toScreenY(smoothedPoints[idx1+2]));
          ctx.lineTo(toScreenX(smoothedPoints[idx2]), toScreenY(smoothedPoints[idx2+2]));
          ctx.strokeStyle = `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
          ctx.lineWidth = 2.2;
          ctx.stroke();
        }

        // Draw Live Head Marker (Latest point) is removed to avoid overlap
      }

      ctx.restore(); // END CLIPPED REGION

      // 4. DRAW FIXED BORDER FRAME
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(boxLeft, boxTop, boxW, boxH);

            // 5. DRAW AXIS TICKS AND VALUES ON FIXED FRAME BORDERS
      const drawnRects: {l: number, r: number, t: number, b: number}[] = [];
      const checkCollision = (text: string, tx: number, ty: number): boolean => {
        const metrics = ctx.measureText(text);
        let l = tx, r = tx, t = ty, b = ty;
        const align = ctx.textAlign;
        const baseline = ctx.textBaseline;
        if (align === 'center') {
          l = tx - metrics.width / 2; r = tx + metrics.width / 2;
        } else if (align === 'right' || align === 'end') {
          l = tx - metrics.width; r = tx;
        } else {
          l = tx; r = tx + metrics.width;
        }
        if (baseline === 'middle') {
          t = ty - 6; b = ty + 6;
        } else if (baseline === 'bottom' || baseline === 'alphabetic') {
          t = ty - 12; b = ty;
        } else if (baseline === 'top') {
          t = ty; b = ty + 12;
        }
        const pad = 6;
        for (const rect of drawnRects) {
          if (l < rect.r + pad && r > rect.l - pad && t < rect.b + pad && b > rect.t - pad) return true;
        }
        drawnRects.push({l, r, t, b});
        return false;
      };

      const drawTextOutline = (text: string, tx: number, ty: number, col = 'rgba(255, 255, 255, 0.85)') => {
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#000000';
        ctx.strokeText(text, tx, ty);
        ctx.fillStyle = col;
        ctx.fillText(text, tx, ty);
      };

      // Pre-calculate live tracker coordinates and register their bounding boxes to prevent tick overlap
      let liveTrackerInfo: {
        currPx: number;
        currPy: number;
        trackerColor: string;
        momTxt: string;
        timeTxt: string;
        momBoxWidth: number;
        timeBoxWidth: number;
      } | null = null;

      if (data && data.length > 0) {
        const last = data[data.length - 1];
        const lastTime = last.timestamp || Date.now();
        const lastMom = last.momentum || 0;
        const isUp = lastMom >= 0;
        const trackerColor = isUp ? buyersColor : sellersColor;

        let currPx = toScreenX(timeToDataX(lastTime));
        let currPy = toScreenY(lastMom * zScale);

        if (smoothedPtsCount > 0) {
          const lastIdx = (smoothedPtsCount - 1) * 3;
          currPx = toScreenX(smoothedPoints[lastIdx]);
          currPy = toScreenY(smoothedPoints[lastIdx+2]);
        }

        const mDecimals = Math.abs(lastMom) >= 1000 ? 1 : (Math.abs(lastMom) >= 100 ? 1 : (Math.abs(lastMom) >= 1 ? 2 : 4));
        const momTxt = formatAbbrev(lastMom, mDecimals);

        const d = new Date(lastTime);
        const timeTxt = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

        ctx.font = 'bold 11px "Inter", ui-sans-serif, system-ui, sans-serif';
        const padX = 6;
        const momBoxWidth = ctx.measureText(momTxt).width + padX * 2;
        const timeBoxWidth = ctx.measureText(timeTxt).width + padX * 2;

        liveTrackerInfo = {
          currPx,
          currPy,
          trackerColor,
          momTxt,
          timeTxt,
          momBoxWidth,
          timeBoxWidth
        };

        // Reserve space in collision map so no axis ticks render under the live boxes
        drawnRects.push({
          l: boxRight - momBoxWidth - 6,
          r: boxRight,
          t: currPy - 12,
          b: currPy + 12
        });
        drawnRects.push({
          l: currPx - timeBoxWidth / 2 - 6,
          r: currPx + timeBoxWidth / 2 + 6,
          t: boxBottom - 24,
          b: boxBottom
        });
      }

      ctx.font = '600 11px "Inter", ui-sans-serif, system-ui, sans-serif';

      // X-Axis Numerical Ticks (Time, along fixed bottom of box)
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      const visTimeRange = Math.max(1000, visTimeMax - visTimeMin);
      for (let t = firstTime; t <= visTimeMax + timeStep * 0.5; t += timeStep) {
        const px = toScreenX(timeToDataX(t));
        if (px >= boxLeft && px <= boxRight) {
          const txt = formatTime(t, visTimeRange);
          if (checkCollision(txt, px, boxBottom - 8)) continue;

          ctx.beginPath();
          ctx.moveTo(px, boxBottom);
          ctx.lineTo(px, boxBottom - 5);
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
          ctx.lineWidth = 1;
          ctx.stroke();

          drawTextOutline(txt, px, boxBottom - 8, 'rgba(255, 255, 255, 0.8)');
        }
      }

      // Y-Axis Numerical Ticks (Momentum, along fixed right of box)
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      for (let m = firstMom; m <= realVisMomMax + momStep * 0.5; m += momStep) {
        const py = toScreenY(m * zScale);
        if (py >= boxTop && py <= boxBottom) {
          const mDecimals = momStep < 0.01 ? 4 : (momStep < 0.1 ? 3 : 2);
          const txt = formatAbbrev(m, mDecimals);
          if (checkCollision(txt, boxRight - 8, py)) continue;

          ctx.beginPath();
          ctx.moveTo(boxRight, py);
          ctx.lineTo(boxRight - 5, py);
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
          ctx.lineWidth = 1;
          ctx.stroke();

          const labelCol = m > 0.001 ? buyersColor : (m < -0.001 ? sellersColor : 'rgba(255, 255, 255, 0.7)');
          drawTextOutline(txt, boxRight - 8, py, labelCol);
        }
      }

      // --- DRAW CURRENT VALUE TRACKERS (Drawn last on top of axes) ---
      if (liveTrackerInfo) {
        const { currPx, currPy, trackerColor, momTxt, timeTxt, momBoxWidth, timeBoxWidth } = liveTrackerInfo;

        // Draw horizontal dashed line
        ctx.beginPath();
        ctx.setLineDash([4, 4]);
        ctx.moveTo(currPx, currPy);
        ctx.lineTo(boxRight, currPy);
        ctx.strokeStyle = trackerColor;
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw vertical dashed line for Time
        ctx.beginPath();
        ctx.moveTo(currPx, currPy);
        ctx.lineTo(currPx, boxBottom);
        ctx.stroke();
        ctx.setLineDash([]);

        // Draw Momentum Badge (Right Axis)
        ctx.font = 'bold 11px "Inter", ui-sans-serif, system-ui, sans-serif';
        ctx.fillStyle = trackerColor;
        ctx.fillRect(boxRight - momBoxWidth, currPy - 10, momBoxWidth, 20);
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(momTxt, boxRight - momBoxWidth / 2, currPy);

        // Draw Time Badge (Bottom Axis)
        ctx.fillStyle = trackerColor;
        ctx.fillRect(currPx - timeBoxWidth / 2, boxBottom - 20, timeBoxWidth, 20);
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(timeTxt, currPx, boxBottom - 10);
      }
      
    };

    drawRef.current = draw;
    let animFrame = requestAnimationFrame(draw);

    const handleResize = () => draw();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animFrame);
    };
  }, [isOpen, smoothedPoints, smoothedColorsFloor, gridColor, gridOpacity, width3D, zLimit3D, zScale, buyersColor, sellersColor, timeMin, timeMax, timeDiff, startX]);

  const activePointers = useRef<Map<number, {x: number, y: number}>>(new Map());
  const initialPinchDistance = useRef<number | null>(null);
  const initialPinchZoom = useRef<number>(1);

  const handlePointerDown = (e: React.PointerEvent) => {
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture(e.pointerId);

    if (activePointers.current.size === 2) {
      const pts = Array.from(activePointers.current.values());
      initialPinchDistance.current = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      initialPinchZoom.current = transformRef.current.zoom;
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!activePointers.current.has(e.pointerId)) return;
    
    if (activePointers.current.size === 1) {
      const lastPos = activePointers.current.get(e.pointerId)!;
      const dx = e.clientX - lastPos.x;
      const dy = e.clientY - lastPos.y;
      transformRef.current.panX += dx;
      transformRef.current.panY += dy;
      activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (drawRef.current) requestAnimationFrame(drawRef.current);
    } else if (activePointers.current.size === 2) {
      activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const pts = Array.from(activePointers.current.values());
      const currentDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      
      if (initialPinchDistance.current && initialPinchDistance.current > 0) {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;

        const margin = { left: 0, right: 0, top: 0, bottom: 0 };
        const boxLeft = margin.left;
        const boxTop = margin.top;
        const boxW = Math.max(100, rect.width - margin.left - margin.right);
        const boxH = Math.max(100, rect.height - margin.top - margin.bottom);
        const defaultCenterX = boxLeft + boxW / 2;
        const defaultCenterY = boxTop + boxH / 2;

        const centerX = (pts[0].x + pts[1].x) / 2 - rect.left;
        const centerY = (pts[0].y + pts[1].y) / 2 - rect.top;
        
        const zoomFactor = currentDist / initialPinchDistance.current;
        const { panX, panY } = transformRef.current;
        
        const newZoom = Math.max(0.3, Math.min(initialPinchZoom.current * zoomFactor, 50));
        const actualFactor = newZoom / transformRef.current.zoom;
        
        const mouseRelX = centerX - (defaultCenterX + panX);
        const mouseRelY = centerY - (defaultCenterY + panY);
        
        transformRef.current.panX = panX - mouseRelX * (actualFactor - 1);
        transformRef.current.panY = panY - mouseRelY * (actualFactor - 1);
        transformRef.current.zoom = newZoom;
        
        if (drawRef.current) requestAnimationFrame(drawRef.current);
      }
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    activePointers.current.delete(e.pointerId);
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (activePointers.current.size < 2) {
      initialPinchDistance.current = null;
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const zoomSensitivity = 0.002;
    const delta = -e.deltaY * zoomSensitivity;
    const zoomFactor = Math.exp(delta);
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    const margin = { left: 0, right: 0, top: 0, bottom: 0 };
    const boxLeft = margin.left;
    const boxTop = margin.top;
    const boxW = Math.max(100, rect.width - margin.left - margin.right);
    const boxH = Math.max(100, rect.height - margin.top - margin.bottom);
    const defaultCenterX = boxLeft + boxW / 2;
    const defaultCenterY = boxTop + boxH / 2;
    
    const { panX, panY, zoom } = transformRef.current;
    const newZoom = Math.max(0.3, Math.min(zoom * zoomFactor, 50));
    const actualFactor = newZoom / zoom;
    
    // Distance from mouse to current virtual center
    const mouseRelX = mouseX - (defaultCenterX + panX);
    const mouseRelY = mouseY - (defaultCenterY + panY);
    
    // Adjust pan to keep the content under the mouse fixed
    transformRef.current.panX = panX - mouseRelX * (actualFactor - 1);
    transformRef.current.panY = panY - mouseRelY * (actualFactor - 1);
    transformRef.current.zoom = newZoom;
    
    if (drawRef.current) requestAnimationFrame(drawRef.current);
  };

  const handleDoubleClick = () => {
    resetTransform();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-black font-mono text-white select-none">
      {/* Header Bar */}
      <div className="absolute top-0 inset-x-0 z-20 flex justify-between items-start p-3 pt-[calc(0.75rem+env(safe-area-inset-top,0px))] bg-gradient-to-b from-black/70 to-transparent pointer-events-none [&>*]:pointer-events-auto">
        <CoreHUD 
          isEditingSymbol={isEditingSymbol!}
          setIsEditingSymbol={setIsEditingSymbol!}
          symbol={symbol!}
          setSymbol={setSymbol!}
          timeframe={timeframe!}
          lastPrice={lastPrice!}
          priceChange={priceChange!}
          currentMomentum={data && data.length > 0 ? data[data.length - 1].momentum : null}
          candleCloseTime={data && data.length > 0 ? data[data.length - 1].closeTime : undefined}
          latencyMs={latencyMs!}
        />
        <div className="flex items-center gap-2 shrink-0">
          <button 
            onClick={resetTransform}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white/5 hover:bg-white/15 border border-white/10 rounded-md transition-colors text-zinc-300 hover:text-white"
            title="Restaurar zoom e posição padrão"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Centralizar</span>
          </button>
          <button 
            onClick={onClose} 
            className="p-1.5 hover:bg-white/10 rounded-md transition-colors text-zinc-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      <div ref={wrapperRef} className="absolute top-0 left-[env(safe-area-inset-left,0px)] right-[env(safe-area-inset-right,0px)] bottom-[env(safe-area-inset-bottom,0px)] overflow-hidden cursor-grab active:cursor-grabbing">
        <div 
          className="absolute inset-0 pointer-events-none transition-opacity duration-1000 ease-in-out z-10" 
          style={{ opacity: labelOpacity }}
        >
          {/* Y Axis Label */}
          <div className="absolute right-16 top-1/2 -translate-y-1/2 -rotate-90 flex items-center justify-center origin-center h-0 w-0">
            <div className="text-[11px] font-semibold text-white/35 tracking-wider whitespace-nowrap select-none">
              MOMENTUM
            </div>
          </div>
          {/* X Axis Label */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-[11px] font-semibold text-white/35 tracking-wider whitespace-nowrap select-none">
            TEMPO
          </div>
        </div>
        
        {/* Permanent Zone Labels */}
        <div className="absolute inset-0 pointer-events-none z-10">
          <div className="absolute top-[25%] left-4 -translate-y-1/2 -rotate-90 flex items-center justify-center origin-center h-0 w-0">
            <div className="text-[11px] font-semibold text-rose-400/70 tracking-wider whitespace-nowrap">
              ▶ VENDEDORES (-)
            </div>
          </div>
          <div className="absolute bottom-[25%] left-4 translate-y-1/2 -rotate-90 flex items-center justify-center origin-center h-0 w-0">
            <div className="text-[11px] font-semibold text-emerald-400/70 tracking-wider whitespace-nowrap">
              ◀ COMPRADORES (+)
            </div>
          </div>
        </div>
        
        <canvas 
          ref={canvasRef} 
          className="absolute inset-0 w-full h-full touch-none"
          
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onWheel={handleWheel}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />

        
      </div>
    </div>
  );
};
